import { useEffect, useRef } from 'react';
import { CHARACTER_SELECT_EXPLOSION_MS, CHARACTER_SELECT_FIGHT_MS, CHARACTER_SELECT_LEFT_MOVES_MS, CHARACTER_SELECT_READY_MS, CHARACTER_SELECT_RIGHT_MOVES_MS, VERSUS_EXPLOSION_MS, VERSUS_FIGHT_MS } from '../data/characterSelect';
import { attractAudio, audioAsset } from '../lib/attractAudio';
import type { AttractEntry } from '../types';

const characterVoice = (id: string) => audioAsset(`/assets/audio/characters/${id}.wav`);
const announcerName = (id: string) => audioAsset(`/assets/audio/announcer/names/${id}.wav`);
const cue = (path: string) => audioAsset(`/assets/audio/${path}`);
const loreMusic = cue('music/dread-signal-loop.wav');
const smackersMusic = cue('music/smackers-night-drive-loop.wav');
const tournamentMusic = cue('music/island-circuit-loop.wav');
const bossIds = new Set(['the-cheater', 'the-convergence']);

interface AttractAudioOptions {
  enabled: boolean;
  entry: AttractEntry;
  entryIndex: number;
  sceneElapsedMs: number;
  nextEntry?: AttractEntry;
  playbackMultiplier: number;
}

export function useAttractAudio({ enabled, entry, entryIndex, playbackMultiplier, sceneElapsedMs, nextEntry }: AttractAudioOptions) {
  const selectMoveRef = useRef({ left: 0, right: 0 });
  const readyPlayedRef = useRef(false);
  const fightPlayedRef = useRef(false);
  const explosionPlayedRef = useRef(false);
  const easterCuePlayedRef = useRef(false);

  useEffect(() => {
    attractAudio.setEnabled(enabled);
    return () => attractAudio.setEnabled(false);
  }, [enabled]);

  useEffect(() => {
    attractAudio.setPlaybackMultiplier(playbackMultiplier);
  }, [playbackMultiplier]);

  useEffect(() => {
    if (enabled) attractAudio.prime([loreMusic, smackersMusic, tournamentMusic]);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    selectMoveRef.current = { left: 0, right: 0 };
    readyPlayedRef.current = false;
    fightPlayedRef.current = false;
    explosionPlayedRef.current = false;
    easterCuePlayedRef.current = false;

    attractAudio.requestScene(() => {
      if (entry.type === 'character') {
        void attractAudio.play(cue('ui/profile-hit.wav'), { kind: 'effect', gain: 0.75 });
        void attractAudio.play(announcerName(entry.characterId), { kind: 'announcer', delaySeconds: 0.24, playbackRate: 0.9 });
      } else if (entry.type === 'title') {
        void attractAudio.play(cue('ui/title-sting.wav'), { kind: 'effect', gain: 0.85 });
      } else if (entry.type === 'story') {
        void attractAudio.play(cue('ui/story-pulse.wav'), { kind: 'effect', gain: 0.52 });
      } else if (entry.type === 'versus') {
        void attractAudio.play(cue('ui/versus-impact.wav'), { kind: 'effect', gain: 0.9 });
        void attractAudio.playSequence([
          { url: announcerName(entry.leftCharacterId), playbackRate: 0.9 },
          cue('announcer/versus.wav'),
          { url: announcerName(entry.rightCharacterId), playbackRate: 0.9 },
        ], { kind: 'announcer', delaySeconds: 0.62, gapSeconds: 0.1, gain: 1 });
      } else if (entry.type === 'character-select') {
        attractAudio.playIfUnlocked(cue('ui/roster-step.wav'), { kind: 'effect', playbackRate: 0.9 });
      } else {
        void attractAudio.play(cue('ui/finale-sting.wav'), { kind: 'effect', gain: 0.92 });
        void attractAudio.play(announcerName('the-convergence'), { kind: 'announcer', delaySeconds: 0.72, gain: 1, playbackRate: 0.9 });
      }
    });

    const preload: string[] = [];
    if (nextEntry?.type === 'character') preload.push(announcerName(nextEntry.characterId), characterVoice(nextEntry.characterId));
    if (nextEntry?.type === 'versus') {
      preload.push(announcerName(nextEntry.leftCharacterId), announcerName(nextEntry.rightCharacterId), cue('announcer/versus.wav'));
    }
    attractAudio.prime(preload);
    return () => attractAudio.stopScene();
  }, [enabled, entry, entryIndex, nextEntry]);

  useEffect(() => {
    const bossProfile = entry.type === 'character' && bossIds.has(entry.characterId);
    const bossVersus = entry.type === 'versus' && (bossIds.has(entry.leftCharacterId) || bossIds.has(entry.rightCharacterId));
    const score = entry.type === 'story' || entry.type === 'finale' || bossProfile || bossVersus
      ? loreMusic
      : entry.type === 'character'
        ? smackersMusic
        : tournamentMusic;
    attractAudio.setMusic(enabled ? score : null);
  }, [enabled, entry]);

  useEffect(() => {
    if (!enabled || (entry.type !== 'character-select' && entry.type !== 'versus')) return;
    if (entry.type === 'character-select') {
      const moves = {
        left: CHARACTER_SELECT_LEFT_MOVES_MS.filter((time) => sceneElapsedMs >= time).length,
        right: CHARACTER_SELECT_RIGHT_MOVES_MS.filter((time) => sceneElapsedMs >= time).length,
      };
      const leftPitch = [0.86, 1.03, 0.92, 1.1];
      const rightPitch = [1.12, 0.95, 1.06, 0.89];
      const leftStart = selectMoveRef.current.left;
      const rightStart = selectMoveRef.current.right;
      for (let index = leftStart; index < moves.left; index += 1) {
        attractAudio.playIfUnlocked(cue('ui/roster-step.wav'), { kind: 'effect', delaySeconds: (index - leftStart) * 0.08, playbackRate: leftPitch[index % leftPitch.length], gain: 0.94 });
      }
      for (let index = rightStart; index < moves.right; index += 1) {
        attractAudio.playIfUnlocked(cue('ui/roster-step.wav'), { kind: 'effect', delaySeconds: (index - rightStart) * 0.08 + 0.035, playbackRate: rightPitch[index % rightPitch.length], gain: 0.88 });
      }
      selectMoveRef.current = moves;
      if (sceneElapsedMs >= CHARACTER_SELECT_READY_MS && !readyPlayedRef.current) {
        readyPlayedRef.current = true;
        attractAudio.playIfUnlocked(cue('ui/ready.wav'), { kind: 'effect', gain: 0.75 });
        attractAudio.playIfUnlocked(cue('announcer/ready.wav'), { kind: 'announcer', delaySeconds: 0.08 });
      }
    }
    const fightAt = entry.type === 'versus' ? VERSUS_FIGHT_MS : CHARACTER_SELECT_FIGHT_MS;
    if (sceneElapsedMs >= fightAt && !fightPlayedRef.current) {
      fightPlayedRef.current = true;
      attractAudio.playIfUnlocked(cue('ui/fight-impact.wav'), { kind: 'effect', gain: 0.92 });
      attractAudio.playIfUnlocked(cue('announcer/fight.wav'), { kind: 'announcer', delaySeconds: 0.05, gain: 1 });
    }
    const explosionAt = entry.type === 'versus' ? VERSUS_EXPLOSION_MS : CHARACTER_SELECT_EXPLOSION_MS;
    if (sceneElapsedMs >= explosionAt && !explosionPlayedRef.current) {
      explosionPlayedRef.current = true;
      attractAudio.playIfUnlocked(cue('ui/comic-burst.wav'), { kind: 'effect', gain: 0.95 });
    }
  }, [enabled, entry, sceneElapsedMs]);

  useEffect(() => {
    if (!enabled || entry.type !== 'versus' || !entry.easterEgg || sceneElapsedMs < 4500 || easterCuePlayedRef.current) return;
    easterCuePlayedRef.current = true;
    attractAudio.requestScene(() => {
      void attractAudio.play(cue('easter-eggs/vandal-unacceptable.wav'), { kind: 'voice', gain: 0.95, playbackRate: 1.35 });
    });
  }, [enabled, entry, sceneElapsedMs]);
}
